import React, { useEffect, useState } from "react";
import { ArrayInput, SimpleFormIterator, useSimpleFormIteratorItem, TextInput, useNotify, FormDataConsumer, required } from "react-admin";
import { useDataProvider } from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import { settings } from "app";
import { Grid, Typography } from "@mui/material";
import { VACATION_PERIODS_LIMIT } from "utils/constants";
import { administrationRequests } from "api/administrationApi";
import { validateEnd, validateStart, validateDateInAMonth } from 'components/Leaves/Requests/utils/validations';
import { useIsSubstituteRequired } from 'components/Leaves/Requests/utils/useIsSubstituteRequired';

import CustomDateInput from "server/components/Fields/CustomDateInput";
import SubstituteInput from "./SubstituteInput";

const PeriodRow = ({ isSubstituteRequired }) => {
  const { index } = useSimpleFormIteratorItem();
  const dataProvider = useDataProvider();
  const { setValue } = useFormContext();
  const [daysCount, setDaysCount] = useState(null);
  const employeeId = useWatch({ name: "employeeId" });
  const dateTo = useWatch({ name: `periods.${index}.dateTo` });
  const dateFrom = useWatch({ name: `periods.${index}.dateFrom` });
  const notify = useNotify();

  useEffect(() => {
    if (dateTo && dateFrom) {
      dataProvider.customRequest(
        administrationRequests.getWorkingDaysCount({ dateFrom, dateTo }),
        null,
        "GET",
      )
        .then(response => {
          setValue(`periods.${index}.numberOfDays`, response.data);
          setDaysCount(response.data);
        })
        .catch(error => {
          console.error(error);
          setValue(`periods.${index}.numberOfDays`, '0');
          setDaysCount('0');
        })
        .finally(() => {
        });
    }
  }, [dateTo, dateFrom, index, dataProvider, setValue, notify]);

  return (
    <>
      <Typography variant="subtitle1" sx={{ marginBottom: '10px', fontWeight: 500 }}>
        Период {index + 1}
      </Typography>
      <FormDataConsumer>
        {({ scopedFormData }) => {
          return (
            <Grid container spacing={1}>
              <Grid item xs={10}>
                <Grid item xs={12}>
                  <CustomDateInput
                    source="dateFrom"
                    label="От дата"
                    required={true}
                    noTime
                    validate={[
                      () =>
                        validateStart(
                          scopedFormData.dateFrom,
                          scopedFormData.dateTo,
                          required,
                          "Началният ден не може да бъде по-голям от крайният",
                        ),
                      () => validateDateInAMonth(scopedFormData.dateFrom),
                    ]}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CustomDateInput
                    source="dateTo"
                    label="До дата"
                    required={true}
                    noTime
                    minDate={scopedFormData?.dateFrom}
                    validate={() =>
                      validateEnd(
                        scopedFormData.dateTo,
                        scopedFormData.dateFrom,
                        required,
                        "Крайният ден не може да бъде по-малък от началният",
                      )
                    }
                  />
                </Grid>
                {daysCount &&
                  <Grid item xs={12}>
                    <TextInput readOnly source="numberOfDays" label="Брой работни дни" defaultValue={1} />
                  </Grid>
                }
                <Grid item xs={12}>
                  <SubstituteInput
                    employeeId={employeeId}
                    isRequired={isSubstituteRequired}
                    selectedId={scopedFormData?.substituteEmployeeId}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                  />
                </Grid>
              </Grid>
            </Grid>
          )
        }}
      </FormDataConsumer>
    </>
  );
};

const VacationRequestPeriod = ({ setIsFormValid }) => {
  const periods = useWatch({ name: 'periods' });
  const reasonId = useWatch({ name: 'reasonId' });
  const employeeId = useWatch({ name: 'employeeId' });
  const { isRequired: isSubstituteRequired, isLoading: isSubstituteCheckLoading } =
    useIsSubstituteRequired(employeeId);
  const isFormValid =
    reasonId &&
    !isSubstituteCheckLoading &&
    periods?.length > 0 &&
    periods?.every(p => p?.dateFrom && p?.dateTo && (!isSubstituteRequired || p?.substituteEmployeeId));

  useEffect(() => {
    setIsFormValid(isFormValid);
  }, [isFormValid, setIsFormValid]);

  return (
    reasonId && (
      <>
        <ArrayInput
          source="periods"
          label={false}
          defaultValue={[{}]}
        >
          <SimpleFormIterator
            disableAdd={periods?.length >= VACATION_PERIODS_LIMIT}
            sx={settings.style.iteratorWithGap}
          >
            <PeriodRow isSubstituteRequired={isSubstituteRequired} />
          </SimpleFormIterator>
        </ArrayInput>
      </>
    )
  )
}

export default VacationRequestPeriod;